import { LeftOutlined } from '@ant-design/icons';
import { FormSkeleton } from '@src/components';
import {
    USER_MANAGEMENT_FULL_URL,
    USER_MANAGEMENT_ROLE_URL,
} from '@src/constants/URLS.const';
import { useAddRoleMutation } from '@src/services/role/addRole/addRoleApiSlice';
import { useEditRoleMutation } from '@src/services/role/editRole/editRoleApiSlice';
import { useGetRoleByIdQuery } from '@src/services/role/roleById/roleByIdApiSlice';
import { useLazyGetSubAccountListQuery } from '@src/services/subAccount/subAccountList/subAccountListApiSlice';
import { Button, Form, message, Typography } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import InputSection from './components/InputSection';
import { getPermissionsData } from './components/permissionsData';
import PermissionSection from './components/PermissionSection';
import RendererFunctions from './components/RendererFunctions';

const { Text } = Typography;

export default function RoleManagementForm() {
    const { t } = useTranslation();
    const permissionsData = getPermissionsData(t);
    const [form] = Form.useForm();
    const [isFormDirty, setIsFormDirty] = useState(false);
    const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
    const initialEligibleAccountsRef = useRef<string[]>([]);
    const isFormInitializedRef = useRef(false);
    const navigate = useNavigate();
    const { roleId } = useParams();
    const [getSubAccount, { data, isFetching }] =
        useLazyGetSubAccountListQuery();
    const [addRole, { isLoading: isAddingRole }] = useAddRoleMutation();
    const [updateRole, { isLoading: isUpdatingRole }] = useEditRoleMutation();

    // Fixed the skip condition - only skip when not in edit mode or no roleId
    const {
        data: roleData,
        isLoading: isLoadingRole,
        error: roleError,
        refetch: refetchRole,
    } = useGetRoleByIdQuery(
        { roleId: roleId ?? '' },
        { skip: !roleId } // Only skip if no roleId
    );

    const [userOptions, setUserOptions] = useState<
        Record<string, string | number>[]
    >([]);

    const location = useLocation();
    const isEditMode = location.pathname.includes('/edit/');
    const isLoading = isAddingRole || isUpdatingRole;

    useEffect(() => {
        if (isEditMode && roleId) {
            refetchRole();
        }
    }, [isEditMode, roleId, refetchRole]);

    const fetchUsers = async (searchValue = '') => {
        try {
            const result = await getSubAccount({
                page: 1,
                limit: 50,
                search: searchValue,
            }).unwrap();

            const seenIds = new Set();
            const options = result?.data?.items?.map((user) => ({
                label: user.name,
                value: user.id,
            }));

            setUserOptions(options ?? []);
        } catch (error) {
            console.error('Failed to fetch users:', error);
            message.error('Failed to load users');
        }
    };

    const [permissions, setPermissions] = useState(permissionsData);

    useEffect(() => {
        if (!isEditMode) {
            fetchUsers();
        }
    }, [isEditMode]);

    useEffect(() => {
        const initializeForm = async () => {
            if (isEditMode && roleId && roleData?.data) {
                try {
                    // Set form values
                    form.setFieldsValue({
                        roleName: roleData.data.name,
                        roleDescription: roleData.data.description || '',
                        eligibleAccounts: roleData.data.eligibleAccounts || [],
                    });

                    // Set selected accounts
                    const accountsAsStrings = (
                        roleData.data.eligibleAccounts || []
                    ).map((account) =>
                        typeof account === 'string' ? account : String(account)
                    );
                    setSelectedAccounts(accountsAsStrings);
                    // Store initial value for dirty checking
                    initialEligibleAccountsRef.current = accountsAsStrings;
                    // Mark form as initialized and reset dirty state
                    isFormInitializedRef.current = true;
                    setIsFormDirty(false);

                    // Populate permissions
                    if (roleData.data.permissions) {
                        populatePermissions(roleData.data.permissions);
                    }

                    // Load initial users for the select dropdown
                    fetchUsers();
                } catch (error) {
                    console.error(
                        'Error initializing form with role data:',
                        error
                    );
                    message.error('Failed to load role data');
                }
            }
        };

        initializeForm(); // ✅ Call the async wrapper
    }, [isEditMode, roleId, roleData?.data, form]);

    // Handle role loading errors
    useEffect(() => {
        if (roleError) {
            console.error('Error loading role:', roleError);
            message.error('Failed to load role data. Please try again.');
        }
    }, [roleError]);

    const populatePermissions = (rolePermissions: Record<string, string>) => {
        setPermissions((prev) => {
            return prev.map((permission) => {
                // Handle special case for Parent-UserManagement
                const permissionKey =
                    permission.key === 'Parent-UserManagement'
                        ? 'UserManagement'
                        : permission.key;

                const permissionAccess =
                    rolePermissions[permissionKey] || 'none';

                const updatedChildren = permission.children.map((child) => ({
                    ...child,
                    access: rolePermissions[child.key] || 'none',
                }));

                // Calculate parent access based on children for parent permissions
                let parentAccess = permissionAccess;
                if (permission.children.length > 0) {
                    const childAccesses = updatedChildren
                        .map((child) => child.access)
                        .filter((access) => access !== 'none');

                    const uniqueAccesses = [...new Set(childAccesses)];

                    if (uniqueAccesses.length > 1) {
                        parentAccess = 'mixed';
                    } else if (uniqueAccesses.length === 1) {
                        parentAccess = uniqueAccesses[0];
                    } else {
                        parentAccess = permissionAccess;
                    }
                }

                return {
                    ...permission,
                    access: parentAccess,
                    children: updatedChildren,
                };
            });
        });
    };

    // Helper function to get allowed access levels for a permission
    const getAllowedAccessLevels = (
        permissionKey: string,
        isChild: boolean,
        parentAccess?: string
    ) => {
        if (permissionKey === 'Dashboard') {
            return ['viewer', 'none'];
        }

        if (permissionKey === 'ActivityLogManagement') {
            if (parentAccess && parentAccess === 'admin') {
                return ['viewer', 'none'];
            }
            return ['viewer', 'none'];
        }

        return ['viewer', 'admin', 'none'];
    };

    const updatePermission = (
        permissionKey: string,
        newAccess: string,
        isChild: boolean
    ) => {
        const parentAccess =
            isChild && permissionKey === 'ActivityLogManagement'
                ? permissions.find((p) => p.key === 'Parent-UserManagement')
                      ?.access
                : null;

        const allowedLevels = getAllowedAccessLevels(
            permissionKey,
            isChild,
            parentAccess!
        );

        if (!allowedLevels.includes(newAccess)) {
            console.warn(
                `Access level ${newAccess} is not allowed for ${permissionKey}`
            );
            return;
        }

        setPermissions((prev) => {
            let hasChanged = false;
            const updated = prev.map((permission) => {
                if (isChild) {
                    const child = permission.children.find(
                        (c) => c.key === permissionKey
                    );
                    if (child) {
                        if (child.access !== newAccess) {
                            hasChanged = true;
                        }
                        const updatedChildren = permission.children.map(
                            (child) =>
                                child.key === permissionKey
                                    ? { ...child, access: newAccess }
                                    : child
                        );

                        const childAccesses = updatedChildren.map(
                            (child) => child.access
                        );
                        const uniqueAccesses = [
                            ...new Set(
                                childAccesses.filter(
                                    (access) => access !== 'none'
                                )
                            ),
                        ];
                        const parentAccess =
                            uniqueAccesses.length > 1
                                ? 'mixed'
                                : uniqueAccesses.length === 1
                                  ? uniqueAccesses[0]
                                  : 'none';

                        return {
                            ...permission,
                            children: updatedChildren,
                            access: parentAccess,
                        };
                    }
                } else if (permission.key === permissionKey) {
                    if (permission.access !== newAccess) {
                        hasChanged = true;
                    }
                    if (
                        permission.key === 'Parent-UserManagement' &&
                        newAccess === 'admin'
                    ) {
                        const updatedChildren = permission.children.map(
                            (child) => {
                                if (
                                    child.key === 'ActivityLogManagement' &&
                                    child.access === 'admin'
                                ) {
                                    return { ...child, access: 'viewer' };
                                }
                                return { ...child, access: newAccess };
                            }
                        );

                        return {
                            ...permission,
                            access: newAccess,
                            children: updatedChildren,
                        };
                    } else {
                        const updatedChildren = permission.children.map(
                            (child) => ({ ...child, access: newAccess })
                        );
                        return {
                            ...permission,
                            access: newAccess,
                            children: updatedChildren,
                        };
                    }
                }
                return permission;
            });
            if (hasChanged) {
                setIsFormDirty(true);
            }
            return updated;
        });
    };

    // Constraint enforcement effect
    useEffect(() => {
        const userManagementPermission = permissions.find(
            (p) => p.key === 'Parent-UserManagement'
        );
        const activityLogChild = userManagementPermission?.children.find(
            (c) => c.key === 'ActivityLogManagement'
        );

        if (activityLogChild && activityLogChild.access === 'admin') {
            setPermissions((prev) =>
                prev.map((permission) => {
                    if (permission.key === 'Parent-UserManagement') {
                        return {
                            ...permission,
                            children: permission.children.map((child) => {
                                if (child.key === 'ActivityLogManagement') {
                                    return { ...child, access: 'viewer' };
                                }
                                return child;
                            }),
                        };
                    }
                    return permission;
                })
            );
        }
    }, [permissions]);

    const { renderPermissionItem } = RendererFunctions({
        updatePermission,
        permissions,
        getAllowedAccessLevels,
    });

    useEffect(() => {
        if (!isEditMode) {
            form.resetFields();
            setSelectedAccounts([]);
            setPermissions(permissionsData);
            initialEligibleAccountsRef.current = [];
            isFormInitializedRef.current = false;
        }
    }, [isEditMode, form]);

    // Track eligibleAccounts changes in edit mode
    useEffect(() => {
        // Only check if form has been initialized in edit mode
        if (isEditMode && isFormInitializedRef.current) {
            const currentAccounts = [...selectedAccounts].sort().join(',');
            const initialAccounts = [...initialEligibleAccountsRef.current]
                .sort()
                .join(',');
            // Only set dirty if values actually differ
            if (currentAccounts !== initialAccounts) {
                setIsFormDirty(true);
            } else {
                // If accounts match initial, check other fields to determine dirty state
                const otherFieldsTouched = form.isFieldsTouched([
                    'roleName',
                    'roleDescription',
                ]);
                // Only reset to false if no other fields are touched and accounts match
                if (!otherFieldsTouched) {
                    setIsFormDirty(false);
                }
            }
        }
    }, [selectedAccounts, isEditMode, form]);

    const onFinish = async (values: any) => {
        try {
            // Transform permissions to the required format
            const transformedPermissions: Record<string, 'viewer' | 'admin'> =
                {};

            const addPermission = (key: string, access: string) => {
                if (access !== 'none' && access !== 'mixed') {
                    transformedPermissions[key] = access as 'viewer' | 'admin';
                }
            };

            permissions.forEach((permission) => {
                // Handle special mapping for Parent-UserManagement
                const mappedKey =
                    permission.key === 'Parent-UserManagement'
                        ? 'UserManagement'
                        : permission.key;

                addPermission(mappedKey, permission.access);

                permission.children.forEach((child) => {
                    addPermission(child.key, child.access);
                });
            });

            // Ensure eligibleAccounts is always string[]
            const eligibleAccountsAsStrings = (selectedAccounts || []).map(
                (account) =>
                    typeof account === 'string' ? account : String(account)
            );

            const roleData = {
                name: values.roleName,
                permissions: transformedPermissions,
                description: values.roleDescription || '',
                eligibleAccounts: eligibleAccountsAsStrings,
                isGlobalDefault: false,
            };

            if (isEditMode && roleId) {
                const result = await updateRole({
                    roleId: roleId,
                    ...roleData,
                }).unwrap();
                message.success('Role updated successfully');
            } else {
                const result = await addRole(roleData).unwrap();
                message.success('Role created successfully');
            }

            navigate(`${USER_MANAGEMENT_FULL_URL}/${USER_MANAGEMENT_ROLE_URL}`);
        } catch (error: any) {
            console.error(
                `Failed to ${isEditMode ? 'update' : 'create'} role:`,
                error
            );
            message.error(
                error?.data?.message ||
                    `Failed to ${isEditMode ? 'update' : 'create'} role. Please try again.`
            );
        }
    };

    // Loading state for edit mode
    if (isEditMode && isLoadingRole) {
        return (
            <div className="p-6 mx-auto flex justify-center items-center min-h-64">
                <FormSkeleton />
            </div>
        );
    }

    return (
        <div className="mx-auto">
            {/* Header */}
            <div className="mb-8 flex items-center space-x-3">
                <Button
                    type="text"
                    icon={<LeftOutlined className="border rounded-lg p-2" />}
                    onClick={() =>
                        navigate(
                            `${USER_MANAGEMENT_FULL_URL}/${USER_MANAGEMENT_ROLE_URL}`
                        )
                    }
                    className="flex items-center justify-center"
                />
                <Text className="text-lg font-semibold">
                    {isEditMode ? 'Edit Role' : 'New Role'}
                </Text>
            </div>

            <Form
                requiredMark={false}
                form={form}
                layout="vertical"
                onFinish={onFinish}
                autoComplete="off"
                className="space-y-6"
                onValuesChange={() => {
                    if (!isFormDirty) {
                        setIsFormDirty(form.isFieldsTouched());
                    }
                }}
            >
                {/* Inputs Section */}
                <InputSection
                    form={form}
                    fetchUsers={fetchUsers}
                    getUsers={getSubAccount}
                    userOptions={userOptions}
                    isFetching={isFetching}
                    selectedAccounts={selectedAccounts}
                    setSelectedAccounts={setSelectedAccounts}
                />

                {/* Permissions Section */}
                <PermissionSection
                    permissions={permissions}
                    renderPermissionItem={renderPermissionItem}
                />

                <div className="w-full flex justify-between md:justify-end space-x-2">
                    <Button
                        size="large"
                        onClick={() =>
                            navigate(
                                `${USER_MANAGEMENT_FULL_URL}/${USER_MANAGEMENT_ROLE_URL}`
                            )
                        }
                        className="w-full btn-secondary sm:w-auto !text-gray-500"
                    >
                        {t('buttons.cancel')}
                    </Button>
                    <Button
                        size="large"
                        htmlType="submit"
                        loading={isLoading}
                        disabled={!isFormDirty}
                        className="w-full btn-primary sm:w-auto"
                    >
                        {t('buttons.submit')}
                    </Button>
                </div>
            </Form>
        </div>
    );
}
